/**
 * A cached build per repository of the workspace System (spec
 * `docs/superpowers/specs/2026-10-19-system-cached-builds-slices-design.md`,
 * section 2; Phase 1, first half: per repository, not yet per component).
 *
 * - A **recipe** is what Explore writes per repository: how each of its
 *   components is built (the repository's own Dockerfile, a generated one, or
 *   a pinned image it needs), how it starts, what it listens on, how its
 *   health is checked, how its test data is seeded and where that data lives.
 * - A **build** is one recipe run at one default-branch commit: an image per
 *   component in the Fly registry of the sandbox organisation (by digest), one
 *   data archive in the workspace's own bucket, a manifest, `builtAt`, and
 *   `provenAt` once a fresh start proved it.
 *
 * Nothing here holds a value. A recipe and a manifest name the needs a
 * component reads and the values it generates; the values stay in Fletcher
 * environment revisions and are placed only after a machine starts.
 */
import { z } from "zod";
export const REPO_BUILD_STATES = ["building", "built", "proven", "failed", "superseded"];
/** What started a build: a merge to the default branch, the nightly backstop, Explore, or the operator. */
export const REPO_BUILD_TRIGGERS = ["merge", "nightly", "explore", "operator"];
/** The most a recipe may hold, as stored JSON. */
export const BUILD_RECIPE_MAX_BYTES = 262_144;
/** How many components one repository's recipe may build or start. */
export const BUILD_RECIPE_MAX_COMPONENTS = 30;
const KEY = /^[a-z0-9][a-z0-9._-]{0,62}$/;
const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;
/** A path inside the repository: relative, no `..`, no shell characters. */
const REPO_PATH = /^(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))[A-Za-z0-9._/-]{1,300}$/;
/** An absolute path inside a component's image. */
const IMAGE_PATH = /^\/(?!.*(?:^|\/)\.\.(?:\/|$))[A-Za-z0-9._/-]{0,300}$/;
/** A container image reference: `name[:tag][@sha256:digest]`, no shell characters. */
const IMAGE_REF = /^[a-z0-9][a-z0-9._/:@-]{0,299}$/;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
export const recipeExposeSchema = z.object({
    name: z.string().regex(/^[a-z][a-z0-9-]{0,30}$/),
    port: z.number().int().min(1).max(65535),
    protocol: z.enum(["http", "tcp"]),
});
export const recipeHealthSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("http"),
        port: z.number().int().min(1).max(65535),
        path: z.string().regex(/^\/[A-Za-z0-9._~/?&=%-]{0,300}$/),
        timeoutS: z.number().int().min(1).max(600),
    }),
    z.object({
        kind: z.literal("tcp"),
        port: z.number().int().min(1).max(65535),
        timeoutS: z.number().int().min(1).max(600),
    }),
    z.object({
        kind: z.literal("command"),
        command: z.string().trim().min(1).max(2000),
        timeoutS: z.number().int().min(1).max(600),
    }),
]);
export const recipeSourceSchema = z.discriminatedUnion("kind", [
    /** The repository's own Dockerfile. */
    z.object({
        kind: z.literal("dockerfile"),
        dockerfile: z.string().regex(REPO_PATH),
        context: z.string().regex(/^(\.|(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))[A-Za-z0-9._/-]{1,300})$/),
        target: z
            .string()
            .regex(/^[A-Za-z0-9._-]{1,100}$/)
            .nullable(),
    }),
    /** A Dockerfile Explore wrote from what it did by hand, kept in `generatedDockerfiles`. */
    z.object({ kind: z.literal("generated") }),
    /** A public image the repository needs to run and seed (its database, its cache). */
    z.object({ kind: z.literal("image"), image: z.string().regex(IMAGE_REF) }),
]);
export const recipeComponentSchema = z.object({
    key: z.string().regex(KEY),
    source: recipeSourceSchema,
    /** The start command in place of the image's own; null keeps the image's. */
    start: z.string().trim().min(1).max(2000).nullable(),
    exposes: z.array(recipeExposeSchema).max(20),
    health: recipeHealthSchema,
    /** Run inside the started component to load its test data; null when it needs none. */
    seed: z.object({ command: z.string().trim().min(1).max(2000) }).nullable(),
    /** Where the component keeps its data inside its image; archived after the seed. */
    dataDir: z.string().regex(IMAGE_PATH).nullable(),
    /** The names it reads from provided values (needs), never the values. */
    needs: z.array(z.string().regex(ENV_NAME)).max(100),
    /** The names it generates for itself per start (a session secret, a local password). */
    generated: z.array(z.string().regex(ENV_NAME)).max(100),
    /**
     * Plain settings that are not secrets, such as where its database is
     * (`DB_HOST=mysql`, the other component's key). A name that says secret,
     * token, password or key belongs in `needs` or `generated` instead.
     */
    env: z.record(z.string().regex(ENV_NAME), z.string().max(500)),
    /** The recipe's components it starts after, by key. */
    dependsOn: z.array(z.string().regex(KEY)).max(30),
    memoryMb: z.number().int().positive().max(131_072).nullable(),
});
export const buildRecipeSchema = z
    .object({
    components: z.array(recipeComponentSchema).min(1).max(BUILD_RECIPE_MAX_COMPONENTS),
    generatedDockerfiles: z.record(z.string().regex(KEY), z.string().min(1).max(65_536)),
})
    .superRefine((recipe, ctx) => {
    const keys = new Set();
    for (const component of recipe.components) {
        if (keys.has(component.key)) {
            ctx.addIssue({ code: "custom", message: `${component.key} is named twice` });
        }
        keys.add(component.key);
    }
    for (const component of recipe.components) {
        for (const upstream of component.dependsOn) {
            if (!keys.has(upstream) || upstream === component.key) {
                ctx.addIssue({
                    code: "custom",
                    message: `${component.key} starts after ${upstream}, which the recipe does not build`,
                });
            }
        }
        if (component.source.kind === "generated" && !recipe.generatedDockerfiles[component.key]) {
            ctx.addIssue({
                code: "custom",
                message: `${component.key} uses a generated Dockerfile the recipe does not carry`,
            });
        }
    }
    for (const key of Object.keys(recipe.generatedDockerfiles)) {
        const component = recipe.components.find((c) => c.key === key);
        if (!component || component.source.kind !== "generated") {
            ctx.addIssue({
                code: "custom",
                message: `the generated Dockerfile for ${key} belongs to no generated component`,
            });
        }
    }
});
export const manifestComponentSchema = z.object({
    key: z.string().regex(KEY),
    image: z.object({
        ref: z.string().regex(IMAGE_REF),
        digest: z.string().regex(DIGEST),
    }),
    start: z.string().max(2000).nullable(),
    exposes: z.array(recipeExposeSchema).max(20),
    health: recipeHealthSchema,
    dataDir: z.string().regex(IMAGE_PATH).nullable(),
    needs: z.array(z.string().regex(ENV_NAME)).max(100),
    generated: z.array(z.string().regex(ENV_NAME)).max(100),
    env: z.record(z.string().regex(ENV_NAME), z.string().max(500)),
    dependsOn: z.array(z.string().regex(KEY)).max(30),
    memoryMb: z.number().int().positive().max(131_072).nullable(),
});
/** What a build is: each component's image by digest and how it starts; names only. */
export const buildManifestSchema = z.object({
    components: z.array(manifestComponentSchema).min(1).max(BUILD_RECIPE_MAX_COMPONENTS),
    /** Which provided needs were placed when it was proven (names only). */
    needsUsed: z.array(z.string().regex(ENV_NAME)).max(200),
});
/** The seeded data of one build: an archive in the workspace's own bucket. `volume` is reserved for later. */
export const dataSnapshotSchema = z.object({
    kind: z.literal("archive"),
    key: z.string().regex(/^system\/[A-Za-z0-9._/-]{1,400}$/),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    bytes: z.number().int().nonnegative(),
    takenAt: z.string().datetime(),
});
export const isRepoBuildTrigger = (value) => typeof value === "string" && REPO_BUILD_TRIGGERS.includes(value);
//# sourceMappingURL=systemBuilds.js.map